import { useEffect, useMemo, useState } from 'react';
import {
  PROCESSING_STAGE_LABELS,
  PROCESSING_STAGE_SHORT_LABELS,
  STAGE_MACHINES,
  availableTransitions,
  hasCapability,
  nextWashStage,
  washingPipelineComplete,
  type ProcessingStage,
  type Role,
} from '@cannery/shared';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError } from '../lib/api';
import { Badge, Button, Card, ErrorBanner, Spinner, inputCls } from '../components/ui';
import { Icon } from '../components/Icon';
import { StatusBadge } from './admin/TransactionsPage';

/**
 * Phase 2C - operational laundry processing queue.
 *
 * One screen serves both areas (and the admin's cross-branch view):
 *   area="washing" -> PENDING (start) + WASHING (stage work + handoff)
 *   area="folding" -> FOLDING (count verification + mark ready)
 *
 * Every write goes through the server-validated endpoints; this UI only
 * offers the actions the current role is allowed to attempt, and the
 * server re-checks capability, branch, stage sequence and workflow
 * prerequisites on every call.
 */

export interface ProcessingRecordDto {
  id: string;
  stage: ProcessingStage;
  machine: string | null;
  startedAt: string;
  endedAt: string | null;
  expectedCount: number | null;
  actualCount: number | null;
  difference: number | null;
  note: string | null;
  employeeName: string;
  employeeRole: string;
  createdAt: string;
}

export interface QueueItemDto {
  id: string;
  number: string;
  branch: { id: string; code: string };
  status: 'PENDING' | 'WASHING' | 'FOLDING';
  customerName: string;
  customerPhone: string | null;
  itemCount: number;
  weightGrams: number | null;
  fulfillmentMethod: 'PICKUP' | 'DELIVERY';
  deliveryAddress: string | null;
  createdAt: string;
  processing: ProcessingRecordDto[];
}

interface QueueResponse {
  area: 'washing' | 'folding';
  counts: { waiting: number; active: number };
  items: QueueItemDto[];
}

const WASH_STAGES: ProcessingStage[] = ['INITIAL_WASH', 'FINAL_WASH', 'DRYING'];

const autoWidthCls = inputCls.replace('w-full', '');

function completedStages(item: QueueItemDto): ProcessingStage[] {
  return item.processing
    .filter((r) => r.endedAt)
    .map((r) => r.stage);
}

function openRecord(item: QueueItemDto): ProcessingRecordDto | null {
  return item.processing.find((r) => !r.endedAt) ?? null;
}

/** Expected count for the next step = previous actual (or receiving count). */
function expectedForNext(item: QueueItemDto): number {
  const done = item.processing.filter((r) => r.endedAt);
  if (done.length === 0) return item.itemCount;
  return done[done.length - 1].actualCount ?? item.itemCount;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DeltaBadge({ difference }: { difference: number | null }) {
  if (difference === null || difference === 0) {
    return <Badge tone="emerald">MATCH</Badge>;
  }
  return (
    <Badge tone="rose">
      {difference > 0 ? `+${difference}` : difference} DISCREPANCY
    </Badge>
  );
}

/* ---------------- per-transaction card ---------------- */

function ProcessingCard({
  item,
  area,
  onChanged,
}: {
  item: QueueItemDto;
  area: 'washing' | 'folding';
  onChanged: () => void;
}) {
  const { user, branchFilter } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [machine, setMachine] = useState<Record<string, string>>({});
  const [actual, setActual] = useState<string>('');
  const [note, setNote] = useState<string>('');

  const role = user!.role as Role;
  const done = completedStages(item);
  const open = openRecord(item);
  const nextStage = nextWashStage(done);
  const pipelineDone = washingPipelineComplete(done);
  const hasFoldCount = done.includes('FOLDING');
  const discrepancies = item.processing.filter((r) => r.endedAt && (r.difference ?? 0) !== 0);

  const transitions = availableTransitions(role, item.status);
  const nextTransition = transitions[0];

  const stageAllowed = (stage: ProcessingStage) => hasCapability(role, stageCapability(stage));

  function stageCapability(stage: ProcessingStage) {
    return stage === 'FOLDING' ? 'folding.manage' : 'washing.manage';
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const startStage = (stage: ProcessingStage) =>
    run(async () => {
      const pool = STAGE_MACHINES[stage];
      const chosen = machine[`${item.id}:${stage}`] ?? pool[0];
      await api.post(`/transactions/${item.id}/processing`, {
        stage,
        ...(pool.length > 0 ? { machine: chosen } : {}),
      });
      setActual('');
      setNote('');
    });

  const finishOpen = () =>
    run(async () => {
      if (!open) return;
      const count = Number(actual);
      if (!Number.isInteger(count) || count < 0) {
        throw new ApiError(400, 'VALIDATION_ERROR', 'Enter the actual item count');
      }
      await api.patch(`/transactions/${item.id}/processing/${open.id}`, {
        actualCount: count,
        ...(note ? { note } : {}),
      });
      setActual('');
      setNote('');
    });

  const doTransition = () =>
    run(async () => {
      if (!nextTransition) return;
      await api.patch(`/transactions/${item.id}/status`, { status: nextTransition.to });
    });

  const machineSelect = (stage: ProcessingStage) => {
    const pool = STAGE_MACHINES[stage];
    if (pool.length === 0) return null;
    const key = `${item.id}:${stage}`;
    return (
      <select
        aria-label={`Machine for ${PROCESSING_STAGE_LABELS[stage]}`}
        value={machine[key] ?? pool[0]}
        onChange={(e) => setMachine((m) => ({ ...m, [key]: e.target.value }))}
        className={`${autoWidthCls} w-auto`}
      >
        {pool.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    );
  };

  const handoffBlocked =
    area === 'washing' && item.status === 'WASHING' && (!pipelineDone || open !== null);

  return (
    <Card className="p-5">
      {error && <ErrorBanner message={error} />}

      {/* Header: identification */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900">{item.number}</h3>
            <StatusBadge status={item.status} />
            {branchFilter === 'ALL' && <Badge>{item.branch.code}</Badge>}
          </div>
          <p className="mt-0.5 text-sm text-slate-600">
            {item.customerName}
            {item.customerPhone ? ` · ${item.customerPhone}` : ''}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {item.itemCount} expected items ·{' '}
            {item.fulfillmentMethod === 'DELIVERY'
              ? `Delivery${item.deliveryAddress ? ` — ${item.deliveryAddress}` : ''}`
              : 'Pickup'}{' '}
            · received {formatTime(item.createdAt)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-xs font-semibold tracking-wide text-slate-400 uppercase">
            {item.status === 'PENDING'
              ? 'Waiting to start'
              : item.status === 'WASHING'
                ? 'Washing area'
                : 'Folding area'}
          </span>
          {discrepancies.length > 0 && (
            <Badge tone="rose">
              {discrepancies.length} discrepancy{discrepancies.length > 1 ? 'ies' : ''}
            </Badge>
          )}
        </div>
      </div>

      {/* Stage stepper (washing) */}
      {item.status === 'WASHING' && (
        <ol className="mt-4 flex flex-wrap gap-1.5" aria-label="Washing stages">
          {[...WASH_STAGES, 'ADDITIONAL_DRYING' as const].map((stage) => {
            const count = item.processing.filter(
              (r) => r.stage === stage && r.endedAt,
            ).length;
            const isOpen = open?.stage === stage;
            const isNext = !isOpen && stage === nextStage;
            const label =
              stage === 'ADDITIONAL_DRYING'
                ? `Add. drying${count > 0 ? ` ×${count}` : ''}`
                : PROCESSING_STAGE_SHORT_LABELS[stage];
            return (
              <li
                key={stage}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                  count > 0
                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                    : isOpen
                      ? 'bg-accent text-accent-ink ring-accent'
                      : isNext
                        ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                        : 'bg-slate-100 text-slate-400 ring-slate-200'
                }`}
              >
                {count > 0 && <Icon name="check" className="mr-1 inline h-3 w-3" />}
                {label}
              </li>
            );
          })}
        </ol>
      )}

      {/* Action area */}
      <div className="mt-4 space-y-3">
        {item.status === 'PENDING' && nextTransition && (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={doTransition} disabled={busy} size="lg">
              <Icon name="washing" className="h-5 w-5" />
              {nextTransition.label}
            </Button>
            <p className="text-sm text-slate-500">
              Start the washing process for this order.
            </p>
          </div>
        )}

        {item.status === 'WASHING' &&
          (open ? (
            /* Finish the in-progress step with the actual count. */
            <div className="rounded-xl border border-accent/30 bg-accent-soft p-4">
              <p className="text-sm font-semibold text-accent">
                In progress: {PROCESSING_STAGE_LABELS[open.stage]}
                {open.machine ? ` · machine ${open.machine}` : ''} · started{' '}
                {formatTime(open.startedAt)}
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <label className="text-sm">
                  <span className="block text-slate-600">
                    Expected <b className="text-slate-900">{expectedForNext(item)}</b> · actual count
                  </span>
                  <input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={actual}
                    onChange={(e) => setActual(e.target.value)}
                    placeholder="0"
                    aria-label="Actual item count"
                    className={`${autoWidthCls} mt-1 w-28`}
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-slate-600">Note (optional)</span>
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. one sock short"
                    aria-label="Step note"
                    className={`${autoWidthCls} mt-1 w-56`}
                  />
                </label>
                <Button onClick={finishOpen} disabled={busy}>
                  {busy ? 'Saving…' : 'Save counts'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {nextStage && stageAllowed(nextStage) && (
                <>
                  <span className="text-sm font-medium text-slate-600">
                    Next: {PROCESSING_STAGE_LABELS[nextStage]}
                  </span>
                  {machineSelect(nextStage)}
                  <Button onClick={() => startStage(nextStage)} disabled={busy}>
                    <Icon name="plus" className="h-5 w-5" />
                    Start {PROCESSING_STAGE_SHORT_LABELS[nextStage]}
                  </Button>
                </>
              )}
              {/* Rendered only when no step is open (ternary else-branch),
                  so no additional-drying step can already be in progress. */}
              {pipelineDone &&
                stageAllowed('ADDITIONAL_DRYING') && (
                  <>
                    {machineSelect('ADDITIONAL_DRYING')}
                    <Button variant="secondary" onClick={() => startStage('ADDITIONAL_DRYING')} disabled={busy}>
                      <Icon name="refresh" className="h-5 w-5" />
                      Additional drying
                    </Button>
                  </>
                )}
              {!pipelineDone && (
                <p className="text-sm text-slate-500">
                  Complete initial wash → final wash → drying to hand off.
                </p>
              )}
            </div>
          ))}

        {item.status === 'FOLDING' &&
          (hasFoldCount ? (
            (() => {
              const foldRecord = item.processing.find(
                (r) => r.stage === 'FOLDING' && r.endedAt,
              )!;
              return (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm">
                    Expected <b>{foldRecord.expectedCount}</b> → actual{' '}
                    <b>{foldRecord.actualCount}</b> (Δ{' '}
                    <span
                      className={
                        (foldRecord.difference ?? 0) === 0
                          ? 'font-semibold text-emerald-600'
                          : 'font-semibold text-rose-600'
                      }
                    >
                      {foldRecord.difference! > 0 ? '+' : ''}
                      {foldRecord.difference}
                    </span>
                    )
                  </div>
                  <DeltaBadge difference={foldRecord.difference} />
                  {nextTransition && (
                    <Button onClick={doTransition} disabled={busy} size="lg">
                      {nextTransition.label}
                    </Button>
                  )}
                </div>
              );
            })()
          ) : open ? (
            <div className="rounded-xl border border-accent/30 bg-accent-soft p-4">
              <p className="text-sm font-semibold text-accent">
                Folding count in progress · started {formatTime(open.startedAt)}
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <label className="text-sm">
                  <span className="block text-slate-600">
                    Expected <b className="text-slate-900">{expectedForNext(item)}</b> · actual count
                  </span>
                  <input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={actual}
                    onChange={(e) => setActual(e.target.value)}
                    placeholder="0"
                    aria-label="Actual item count"
                    className={`${autoWidthCls} mt-1 w-28`}
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-slate-600">Note (optional)</span>
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. missing shirt"
                    aria-label="Step note"
                    className={`${autoWidthCls} mt-1 w-56`}
                  />
                </label>
                <Button onClick={finishOpen} disabled={busy}>
                  {busy ? 'Saving…' : 'Save counts'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-slate-600">
                Expected <b className="text-slate-900">{expectedForNext(item)}</b> — count the folded
                items and record the actual quantity.
              </span>
              <Button
                onClick={() => startStage('FOLDING')}
                disabled={busy || !stageAllowed('FOLDING')}
              >
                <Icon name="folding" className="h-5 w-5" />
                Record folding count
              </Button>
            </div>
          ))}

        {/* Handoff (washing area) */}
        {item.status === 'WASHING' && nextTransition && (
          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
            <Button
              variant="secondary"
              size="lg"
              onClick={doTransition}
              disabled={busy || handoffBlocked}
            >
              {nextTransition.label}
            </Button>
            {handoffBlocked && (
              <p className="text-sm text-amber-600">
                {open
                  ? 'Finish the in-progress step first.'
                  : 'Drying must be recorded before handoff.'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Discrepancy visibility */}
      {discrepancies.length > 0 && (
        <div className="mt-3 rounded-xl border border-rose-100 bg-rose-50 p-3">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-rose-700">
            <Icon name="alert" className="h-4 w-4" />
            Item discrepancy recorded
          </p>
          <ul className="mt-1 space-y-0.5 text-sm text-rose-700/90">
            {discrepancies.map((r) => (
              <li key={r.id}>
                {PROCESSING_STAGE_LABELS[r.stage]}: expected {r.expectedCount} · actual{' '}
                {r.actualCount} · Δ {r.difference! > 0 ? '+' : ''}
                {r.difference} ({r.employeeName}, {formatTime(r.endedAt!)})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Processing history (append-only) */}
      {item.processing.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-semibold text-slate-500 select-none">
            Processing history ({item.processing.length})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {item.processing.map((r) => (
              <li
                key={r.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600"
              >
                <span className="font-semibold text-slate-800">
                  {PROCESSING_STAGE_LABELS[r.stage]}
                </span>
                {r.machine && <Badge>{r.machine}</Badge>}
                {r.endedAt ? (
                  <>
                    <span>
                      {r.expectedCount} → {r.actualCount} (Δ{' '}
                      <span
                        className={
                          (r.difference ?? 0) === 0
                            ? 'font-semibold text-emerald-600'
                            : 'font-semibold text-rose-600'
                        }
                      >
                        {r.difference! > 0 ? '+' : ''}
                        {r.difference}
                      </span>
                      )
                    </span>
                    <DeltaBadge difference={r.difference} />
                  </>
                ) : (
                  <span className="font-medium text-indigo-600">in progress…</span>
                )}
                <span className="ml-auto text-slate-400">
                  {r.employeeName} · {formatTime(r.startedAt)}
                  {r.endedAt ? ` → ${formatTime(r.endedAt)}` : ''}
                </span>
                {r.note && <span className="w-full text-slate-500 italic">“{r.note}”</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

/* ---------------- queue page ---------------- */

export function ProcessingQueuePage({ area }: { area: 'washing' | 'folding' }) {
  const { branchFilter, user } = useAuth();
  const [data, setData] = useState<QueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ area });
    // Only admins carry a client-side branch filter. Employees never send a
    // branch param, so the server scopes the queue to their own branch.
    if (user?.role === 'ADMIN' && branchFilter !== 'ALL') params.set('branch', branchFilter);

    void api
      .get<QueueResponse>(`/transactions/queue?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load the queue.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [area, branchFilter, refreshKey, user?.role]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const title = area === 'washing' ? 'Wash and Dry' : 'Folding';
  const waitingLabel = area === 'washing' ? 'To start' : 'To fold';
  const activeLabel = area === 'washing' ? 'In progress' : 'Counted';
  const emptyText =
    area === 'washing'
      ? 'No transactions are waiting for washing.'
      : 'Nothing is waiting for folding.';

  const counts = useMemo(
    () => data?.counts ?? { waiting: 0, active: 0 },
    [data],
  );

  return (
    <section className="space-y-4" aria-label={`${title} processing queue`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {user?.role === 'ADMIN'
              ? branchFilter === 'ALL'
                ? 'All branches'
                : `Branch ${branchFilter}`
              : `Branch ${user?.branchCode ?? ''} · your branch only`}
            {' '}· oldest first ·
            item counts verified at every step.
          </p>
        </div>
        <Button variant="secondary" onClick={refresh}>
          <Icon name="refresh" className="h-5 w-5" />
          Refresh
        </Button>
      </div>

      {error && <ErrorBanner message={error} />}

      {data && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Queue counts">
          <span className="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-ink">
            All · {counts.waiting + counts.active}
          </span>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-300">
            {waitingLabel} · {counts.waiting}
          </span>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-300">
            {activeLabel} · {counts.active}
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-10 text-slate-500">
          <Spinner className="h-5 w-5 text-accent" /> Loading queue…
        </div>
      ) : !data || data.items.length === 0 ? (
        <Card className="flex flex-col items-center p-8 text-center">
          <Icon
            name={area === 'washing' ? 'washing' : 'folding'}
            className="h-8 w-8 text-slate-400"
          />
          <p className="mt-2 font-medium text-slate-700">{emptyText}</p>
          <p className="mt-1 text-sm text-slate-500">
            Transactions appear here automatically as they enter this stage.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.items.map((item) => (
            <ProcessingCard key={item.id} item={item} area={area} onChanged={refresh} />
          ))}
        </div>
      )}
    </section>
  );
}
